import { createContext, useContext, useRef, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import useNotification from "../Components/Notification";

const AutoSaveContext = createContext(null);

const autoSaveMessages = {
  nl: "Automatisch opgeslagen",
  en: "Auto-saved",
  fr: "Sauvegardé automatiquement",
};

export const AutoSaveProvider = ({ children }) => {
  const { triggerNotification, NotificationComponent } = useNotification();
  const location = useLocation();

  const currentRef = useRef(null); // Current page's { save, isDirty }
  const pendingRef = useRef(null); // Captured from previous page on route change
  const prevPathRef = useRef(location.pathname);

  // Detect route change DURING RENDER (before children render and overwrite currentRef)
  if (prevPathRef.current !== location.pathname) {
    pendingRef.current = currentRef.current;
    currentRef.current = null;
    prevPathRef.current = location.pathname;
  }

  // Execute pending auto-save in an effect (after render + children effects)
  useEffect(() => {
    if (pendingRef.current) {
      const { isDirty, save } = pendingRef.current;
      pendingRef.current = null;

      if (isDirty()) {
        const lang = localStorage.getItem("preferredLanguage") || "nl";
        save()
          .then(() => {
            triggerNotification(
              autoSaveMessages[lang] || autoSaveMessages.nl,
              "success"
            );
          })
          .catch((err) => {
            console.error("[AutoSave] Failed:", err);
          });
      }
    }
  }, [location.pathname, triggerNotification]);

  // Prompt the user before closing tab / navigating away from the site
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (currentRef.current) {
        const { isDirty } = currentRef.current;
        if (isDirty()) {
          e.preventDefault();
          e.returnValue = "";
        }
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const register = useCallback((saveFn, isDirtyFn) => {
    currentRef.current = { save: saveFn, isDirty: isDirtyFn };
  }, []);

  return (
    <AutoSaveContext.Provider value={{ register }}>
      <NotificationComponent />
      {children}
    </AutoSaveContext.Provider>
  );
};

/**
 * Hook to register a page's save function for auto-save on navigation away.
 *
 * @param {() => Promise<void>} saveFn  - Async function that saves the page's data
 * @param {boolean}             isDirty - Whether the page has unsaved changes
 */
export const useAutoSave = (saveFn, isDirty) => {
  const ctx = useContext(AutoSaveContext);
  const saveFnRef = useRef(saveFn);
  const isDirtyRef = useRef(isDirty);

  // Keep refs current on every render (synchronous)
  saveFnRef.current = saveFn;
  isDirtyRef.current = isDirty;

  useEffect(() => {
    if (!ctx) return;
    ctx.register(
      () => saveFnRef.current(),
      () => isDirtyRef.current
    );
  }, [ctx]);
};

export default AutoSaveContext;
